import { NativeSelect as ChakraNativeSelect } from '@chakra-ui/react';
import * as React from 'react';

interface NativeSelectItem {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface NativeSelectProps extends Omit<ChakraNativeSelect.FieldProps, 'children'> {
  /** Trigger height dimension. */
  size?: 'sm' | 'md' | 'lg';
  /** Prevents interaction. */
  disabled?: boolean;
  /** Custom indicator icon (replaces default chevron). */
  icon?: React.ReactNode;
  /** Options to render as `<option>` elements. */
  items?: Array<string | NativeSelectItem>;
  /** Manual `<option>` elements (used instead of `items`). */
  children?: React.ReactNode;
  /**
   * Style props forwarded to the Root container (the outermost element).
   * Use for layout props like `width`, `flex`, `minW`, etc. so the chevron
   * indicator stays correctly positioned within the component boundary.
   */
  containerProps?: ChakraNativeSelect.RootProps;
}

export const NativeSelect = React.forwardRef<HTMLSelectElement, NativeSelectProps>(
  function NativeSelect(props, ref) {
    const { size = 'md', disabled, icon, items: itemsProp, children, containerProps, ...rest } = props;

    const items = React.useMemo(
      () => itemsProp?.map(item => (typeof item === 'string' ? { label: item, value: item } : item)),
      [itemsProp]
    );

    return (
      <ChakraNativeSelect.Root disabled={disabled} size={size} {...containerProps}>
        <ChakraNativeSelect.Field ref={ref} {...rest}>
          {children}
          {items?.map(item => (
            <option disabled={item.disabled} key={item.value} value={item.value}>
              {item.label}
            </option>
          ))}
        </ChakraNativeSelect.Field>
        <ChakraNativeSelect.Indicator>{icon}</ChakraNativeSelect.Indicator>
      </ChakraNativeSelect.Root>
    );
  }
);
